import { Dispatch, SetStateAction, useState } from "react";

import { useTranslation } from "react-i18next";

import { AxiosError } from "axios";
import CircularProgress from "@mui/material/CircularProgress";

import { CheckCircleIcon, colors } from "@dotnet/telematics-design-system";

import useAxiosPrivate from "../../../../../../../api/hooks/useAxiosPrivate";
import { ApiResources } from "../../../../../../../api/resources";
import {
  INPUT_LENGTH,
  numbersAndLettersAndDotOnly
} from "../../../../../../../shared";
import { LogisticJournalDataProps } from "../../types";

export const useNewHierarchyFormInputs = (
  logisticJournalData: LogisticJournalDataProps,
  setLogisticJournalData: Dispatch<SetStateAction<LogisticJournalDataProps>>,
  setName: Dispatch<SetStateAction<string>>
) => {
  const { getData } = useAxiosPrivate();
  const { t } = useTranslation();
  const [logisticJournalError, setLogisticJournalError] = useState<
    null | string
  >(null);
  const [verifyButtonLoading, setVerifyButtonLoading] = useState(false);

  const { lzCode, lzCompanyName } = logisticJournalData;

  const handleVerify = async () => {
    setVerifyButtonLoading(true);
    try {
      if (lzCode) {
        setLogisticJournalData({
          ...logisticJournalData,
          lzCompanyName: ""
        });

        const { data } = await getData(
          `${ApiResources.CompaniesLzCode}/${lzCode}`
        );

        const nameFromLZ = data?.name;

        setLogisticJournalData({
          ...logisticJournalData,
          lzCompanyName: nameFromLZ
        });

        setName(nameFromLZ);
      }
    } catch (error) {
      const err = error as AxiosError<any>;

      const errorString = err.response?.data.message || err.message;

      setLogisticJournalError(t(`Alert##${errorString}`));
    }
    setVerifyButtonLoading(false);
  };

  const handleLzCodeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();

    const inputValue = e.target.value;

    if (inputValue.length <= INPUT_LENGTH.LzCodeMaximum) {
      setLogisticJournalData({
        ...logisticJournalData,
        lzCode: e.target.value
      });

      if (numbersAndLettersAndDotOnly.test(inputValue)) {
        setLogisticJournalError(null);
      } else {
        setLogisticJournalError(
          t("Dialog##Teltonika code invalid characters", {
            minCount: INPUT_LENGTH.LzCodeMinimum,
            maxCount: INPUT_LENGTH.LzCodeMaximum
          })
        );
      }
    }
  };

  const getLzCodeFieldIcon = () => {
    if (!lzCompanyName || verifyButtonLoading) return undefined;

    return verifyButtonLoading ? (
      <CircularProgress data-testid="circular-progress" size={20} />
    ) : (
      <CheckCircleIcon color={colors.green600} width={20} height={20} />
    );
  };

  return {
    verifyButtonLoading,
    logisticJournalError,
    handleVerify,
    handleLzCodeInput,
    getLzCodeFieldIcon
  };
};
